import React from "react";

import DefaultBackground from "../assets/image/logo approved.png";
import BottomBackground from "../assets/image/bg.png";

interface PageHeaderProps {
  title?: React.ReactNode;
  slug?: string;
  paragraph?: React.ReactNode;

  // text alignment
  align?: "left" | "center" | "right";

  // custom background image
  backgroundImage?: string;

  // optional custom height
  height?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  slug,
  paragraph,
  align = "center",
  backgroundImage,
  height = "min-h-[220px] sm:min-h-[300px] md:min-h-[380px] lg:min-h-[450px]",
}) => {
  const displayTitle = title || slug?.replace(/-/g, " ");

  // alignment classes
  const alignment = {
    left: {
      container: "items-start text-left",
      justify: "justify-start",
      paragraph: "mr-auto",
      line: "mr-auto",
    },

    center: {
      container: "items-center text-center",
      justify: "justify-center",
      paragraph: "mx-auto",
      line: "mx-auto",
    },

    right: {
      container: "items-end text-right",
      justify: "justify-end",
      paragraph: "ml-auto",
      line: "ml-auto",
    },
  };

  return (
    <div className="relative w-full overflow-hidden bg-white mt-24 sm:mt-0">
      {/* Main Header */}
      <div
        className={`
          relative
          w-full
          flex
          items-center
          justify-center
          overflow-hidden
          bg-no-repeat
          bg-cover
          bg-center
          px-4
          py-10
          sm:py-14
          md:py-16
          ${height}
        `}
        style={{
          backgroundImage: `url(${backgroundImage || DefaultBackground})`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-white/10"></div>

        {/* Content */}
        <div
          className={`
            relative
            z-10
            flex
            flex-col
            w-full
            max-w-7xl
            mx-auto
            sm:mt-[5.5rem]
            ${alignment[align].container}
          `}
        >
          {/* Inner Box */}
          <div className="max-w-3xl">
            {/* Title */}
            <h1
              className={`
    font-semibold
    text-[#1F4D8F]
    uppercase
    tracking-wide
    leading-tight
    drop-shadow-sm
    break-words
    ${
      typeof displayTitle === "string" && displayTitle.length > 35
        ? "text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
        : "text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
    }
  `}
            >
              {typeof displayTitle === "string"
                ? displayTitle.split(" ").map((word, index) => (
                    <span
                      key={index}
                      className={
                        word.toLowerCase().includes("awardee")
                          ? "text-yellow-500"
                          : ""
                      }
                    >
                      {word}{" "}
                    </span>
                  ))
                : displayTitle}
            </h1>

            {/* Decorative Line */}
            <div
              className={`
                flex
                items-center
                gap-3
                mt-4
                ${alignment[align].justify}
              `}
            >
              <span className="w-10 sm:w-14 h-[3px] bg-yellow-400 rounded-full"></span>

              <span className="text-blue-500 text-lg sm:text-xl">★</span>

              <span className="w-10 sm:w-14 h-[3px] bg-yellow-400 rounded-full"></span>
            </div>

            {/* Paragraph */}
            {paragraph && (
              <>
                <div
                  className={`
                    mt-4
                    text-sm
                    sm:text-base
                    md:text-lg
                    text-gray-700
                    leading-relaxed
                    max-w-2xl
                    ${alignment[align].paragraph}
                  `}
                >
                  {paragraph}
                </div>

                {/* Bottom Small Line */}
                <div
                  className={`
                    w-16
                    sm:w-24
                    h-[3px]
                    bg-sky-400
                    rounded-full
                    mt-5
                    ${alignment[align].line}
                  `}
                ></div>
              </>
            )}
          </div>
        </div>

        {/* Bottom Background Image */}
        {!backgroundImage && (
          <div
            className="
              absolute
              bottom-0
              left-0
              w-full
              h-[60px]
              sm:h-[80px]
              md:h-[110px]
              lg:h-[170px]
              bg-no-repeat
              bg-cover
              bg-bottom
              pointer-events-none
            "
            style={{
              backgroundImage: `url(${BottomBackground})`,
            }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
